/**
 * Tiny typed client for the K-Quest Workers API.
 * Reads VITE_API_BASE at build time. Falls back to localhost:8787 in dev.
 */

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? 'http://localhost:8787';

export interface Quest {
  id: string;
  title: string;
  description?: string;
  author_id?: string;
  author_username: string;
  vote_count: number;
  paid_amount_cents: number;
  status?: 'pending' | 'active' | 'rejected' | 'won' | 'done' | 'expired' | 'deferred';
  created_at?: string;
  scheduled_for?: string | null;
  video_url?: string | null;
  locale?: string;
  is_hot?: boolean;
  days_left?: number;
  vetoed_by_user_id?: string | null;
  vetoed_week_iso?: string | null;
}

export interface PastWinner {
  week_iso: string;          // "2026-W18"
  quest: Quest;
  total_votes: number;
  total_paid_cents: number;
  video_published_url: string | null;
  refunded: boolean;
  decided_at: string;
}

export interface AdminOverview {
  funds: {
    this_week_cents: number;
    this_month_cents: number;
    all_time_cents: number;
    pending_settlement_cents: number;
    refunded_all_time_cents: number;
  };
  quests: {
    active: number;
    pending_moderation: number;
    won_this_year: number;
    deferred: number;
  };
  users: {
    total: number;
    active_30d: number;
    banned: number;
  };
  current_week: {
    iso: string;
    deadline_iso: string;
    is_vacation: boolean;
    leader_quest_id: string | null;
  };
}

export interface Moderator {
  id: string;
  username: string;
  email_hash: string;
  role: 'owner' | 'admin' | 'moderator' | 'viewer';
  added_at: string;
  last_action_at: string | null;
}

class ApiError extends Error {
  constructor(public status: number, public code?: string, message?: string) {
    super(message ?? `API ${status}`);
  }
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    let body: { error?: string } = {};
    try { body = await res.json(); } catch { /* not json */ }
    throw new ApiError(res.status, body.error, `${path} -> ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ----- Public ------------------------------------------------------------

export async function fetchActiveQuests(opts: { limit?: number; lang?: string } = {}): Promise<Quest[]> {
  const limit = opts.limit ?? 50;
  const lang = opts.lang ? `&lang=${encodeURIComponent(opts.lang)}` : '';
  const r = await req<{ quests: Quest[] }>(`/api/quests?status=active&limit=${limit}${lang}`);
  return r.quests;
}

export async function fetchPastWinners(opts: { limit?: number; offset?: number; lang?: string } = {}): Promise<PastWinner[]> {
  const limit = opts.limit ?? 24;
  const offset = opts.offset ?? 0;
  const lang = opts.lang ? `&lang=${encodeURIComponent(opts.lang)}` : '';
  const r = await req<{ winners: PastWinner[] }>(`/api/quests/past?limit=${limit}&offset=${offset}${lang}`);
  return r.winners;
}

export interface LeaderRow {
  id: string;
  username: string;
  avatar_url: string | null;
  tier: string | null;
  spent_cents: number;
  votes: number;
  won_quests: number;
}

export async function fetchLeaderboard(limit = 20): Promise<LeaderRow[]> {
  const r = await req<{ leaders: LeaderRow[] }>(`/api/leaderboard?limit=${limit}`);
  return r.leaders;
}

export async function fetchQuest(id: string, lang?: string): Promise<Quest> {
  const q = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  const r = await req<{ quest: Quest }>(`/api/quests/${id}${q}`);
  return r.quest;
}

// Live preview translation (used on the Submit page).
export async function previewTranslation(
  text: string,
  source: string,
  target: string
): Promise<string> {
  const r = await req<{ translated: string }>(`/api/translate/preview`, {
    method: 'POST',
    body: JSON.stringify({ text, source, target }),
  });
  return r.translated;
}

// ----- Admin (require Authorization header) -------------------------------

function authHeaders(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}` };
}

export async function adminOverview(token: string): Promise<AdminOverview> {
  return req<AdminOverview>(`/api/admin/overview`, { headers: authHeaders(token) });
}

export async function adminListModerators(token: string): Promise<Moderator[]> {
  const r = await req<{ moderators: Moderator[] }>(`/api/admin/moderators`, { headers: authHeaders(token) });
  return r.moderators;
}

export async function adminAddModerator(
  token: string,
  payload: { email: string; role: 'admin' | 'moderator' | 'viewer' }
): Promise<{ id: string }> {
  return req(`/api/admin/moderators`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
}

export async function adminListPendingQuests(token: string): Promise<Quest[]> {
  const r = await req<{ quests: Quest[] }>(`/api/admin/quests/pending`, { headers: authHeaders(token) });
  return r.quests;
}

export async function adminApproveQuest(
  token: string,
  questId: string,
  decision: 'approved' | 'rejected',
  reason?: string
): Promise<void> {
  await req(`/api/admin/quests/${questId}/decide`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ decision, reason }),
  });
}

export async function adminMarkVacationWeek(
  token: string,
  weekIso: string,
  reason?: string
): Promise<void> {
  await req(`/api/admin/weeks/${weekIso}/vacation`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ reason }),
  });
}

export async function adminDeferQuest(token: string, questId: string, reason: string): Promise<void> {
  await req(`/api/admin/quests/${questId}/defer`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ reason }),
  });
}

export async function adminPublishVideo(token: string, questId: string, videoUrl: string): Promise<void> {
  await req(`/api/admin/quests/${questId}/publish`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ video_url: videoUrl }),
  });
}

// ----- Auth (magic link) -------------------------------------------------

export async function authRequestLink(email: string): Promise<{ ok: boolean; dev_token?: string }> {
  return req(`/api/auth/request-link`, { method: 'POST', body: JSON.stringify({ email }) });
}

export async function authVerify(token: string): Promise<{ ok: boolean; user_id: string; session_token: string }> {
  return req(`/api/auth/verify?token=${encodeURIComponent(token)}`);
}
